"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchPOSTNext } from "@/utils/fetch-utils";
import { getSafeRedirectUrl } from "@/utils/url-utils";
import { sendOtp } from "../../_logic/auth";
import { OTP_LENGTH, OTP_RESEND_COOLDOWN_SECONDS, OTP_RESEND_TICK_MS, VERIFY_OTP_PATH } from "../../_logic/constants";

/** Index of the last digit box. */
const LAST_OTP_INDEX = OTP_LENGTH - 1;

/**
 * An empty code, one blank string per digit box.
 *
 * @returns A new array of `OTP_LENGTH` blank strings.
 */
const emptyOtp = (): string[] => Array(OTP_LENGTH).fill("");

interface OtpVerificationProps {
  email: string;
  onChangeEmail: () => void;
  redirectTo?: string;
}

/**
 * The second sign-in step: the visitor types the emailed code, and on success the
 * page does a full load to the destination.
 *
 * @param props - The address the code went to, a callback to go back and change it,
 *   and the optional path to open after sign-in.
 * @returns The code entry form.
 */
export const OtpVerification = ({ email, onChangeEmail, redirectTo }: OtpVerificationProps) => {
  // States
  const [otp, setOtp] = useState<string[]>(emptyOtp);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState<number>(0);

  // Derived values
  const canResend = resendTimer === 0;

  // Handlers
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < LAST_OTP_INDEX) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }

    if (e.key === "Enter" && otp.every((digit) => digit !== "")) {
      handleVerifyOtp();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    const digits = pastedData.replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (digits.length === 0) return;

    const newOtp = emptyOtp();
    for (let i = 0; i < digits.length; i++) {
      newOtp[i] = digits[i];
    }
    setOtp(newOtp);

    if (digits.length === OTP_LENGTH) {
      handleVerifyOtp(newOtp);
      return;
    }

    const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
    const focusIndex = nextEmptyIndex === -1 ? LAST_OTP_INDEX : Math.max(0, nextEmptyIndex - 1);

    setTimeout(() => {
      const inputToFocus = document.getElementById(`otp-${focusIndex}`);
      inputToFocus?.focus();
    }, 0);
  };

  const handleVerifyOtp = async (otpOverride?: string[]) => {
    setError(null);
    setIsVerifying(true);
    try {
      const otpToVerify = otpOverride || otp;
      const otpString = otpToVerify.join("");

      // A Route Handler, not a server action. The route explains why.
      const response = await fetchPOSTNext(VERIFY_OTP_PATH, { email, otp: otpString });
      if (!response.ok) throw new Error("Code verification failed");

      // Redirect on the client after successful verification
      window.location.href = getSafeRedirectUrl(redirectTo);
    } catch {
      setError("Something went wrong. Please try again.");
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setError(null);
    setIsResending(true);
    try {
      await sendOtp(email, redirectTo);
      setResendTimer(OTP_RESEND_COOLDOWN_SECONDS);
      setOtp(emptyOtp());
      setTimeout(() => {
        const firstInput = document.getElementById("otp-0");
        firstInput?.focus();
      }, 0);
    } catch {
      // Server-action error messages are redacted in production, so show a generic notice.
      setError("Something went wrong. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  // Effects
  useEffect(() => {
    const firstInput = document.getElementById("otp-0");
    firstInput?.focus();
  }, []);

  useEffect(() => {
    if (resendTimer === 0) return;

    const timer = setTimeout(() => {
      setResendTimer((prev) => prev - 1);
    }, OTP_RESEND_TICK_MS);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  return (
    <div className="flex flex-col gap-12 w-full">
      <button
        onClick={onChangeEmail}
        className="text-sm text-indigo-500 hover:text-indigo-600 hover:underline mr-auto font-medium"
      >
        ← Go back
      </button>

      <div className="flex flex-col gap-2 text-center">
        <h3 className="text-xl font-semibold text-gray-900">Verification code</h3>
        <p className="text-sm text-gray-600 font-medium">
          We have sent a verification code to <span className="font-medium">{email}</span>.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex gap-2 justify-center">
          {otp.map((digit, index) => (
            <FetchInput
              key={index}
              id={`otp-${index}`}
              disabled={isVerifying}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              className="w-12 h-12 text-center font-mono text-lg font-bold rounded-xl"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              onPaste={handleOtpPaste}
            />
          ))}
        </div>

        <div className="text-center">
          {isResending ? (
            <span className="text-sm text-gray-600">Sending new code...</span>
          ) : canResend ? (
            <button
              onClick={handleResendOtp}
              disabled={isVerifying}
              className="text-sm font-medium text-indigo-500 hover:text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed hover:underline"
            >
              Resend code
            </button>
          ) : (
            <span className="text-sm text-gray-600">Resend code in {resendTimer}s</span>
          )}
        </div>

        {error && <p className="text-sm text-red-500 font-medium text-center">{error}</p>}
      </div>

      <FetchButton
        variant="primary"
        color="primary"
        size="lg"
        rightIcon={<ArrowRight className="size-4 text-white" />}
        className="w-full"
        disabled={!otp.every((digit) => digit !== "") || isVerifying}
        loading={isVerifying}
        onClick={() => handleVerifyOtp()}
      >
        Verify code
      </FetchButton>
    </div>
  );
};
